import React, { useState, useEffect, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Copy, Ticket, Check, RotateCcw } from 'lucide-react'

const Receipt = () => {
  const { token } = useParams()
  const [receipt, setReceipt] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(false)
  const intervalRef = useRef(null)

  const fetchReceipt = async () => {
    try {
      const res = await fetch(`/v1/redeem/receipt/${token}`)
      const data = await res.json()
      if (data.success) {
        setReceipt(data.receipt)
        if (['completed', 'failed'].includes(data.receipt.status)) {
          if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null }
        }
      } else {
        setError(data.message || 'Receipt not found')
      }
    } catch { setError('Unable to load receipt') }
    finally { setLoading(false) }
  }

  useEffect(() => {
    if (!token) return
    fetchReceipt()
    intervalRef.current = setInterval(fetchReceipt, 3000)
    const timeout = setTimeout(() => { if (intervalRef.current) clearInterval(intervalRef.current) }, 120000)
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); clearTimeout(timeout) }
  }, [token])

  const formatDate = (d) => d ? new Date(d).toLocaleString('en-US', { dateStyle: 'long', timeStyle: 'short' }) : null

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (loading && !receipt) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 mx-auto mb-3 rounded-full border-3 border-gray-200 border-t-accent animate-spin" style={{ borderWidth: '3px' }}></div>
          <p className="text-text-muted text-sm">Loading...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center max-w-xs">
          <p className="text-5xl mb-4">🔗</p>
          <h2 className="text-lg font-semibold text-text-primary mb-2">Invalid Receipt Link</h2>
          <p className="text-text-muted text-sm mb-6">{error}</p>
          <Link to="/" className="btn btn-primary">Go Home</Link>
        </div>
      </div>
    )
  }

  const navigate = useNavigate()

  if (!receipt) return null

  const isFinal = ['completed', 'failed'].includes(receipt.status)
  const isSuccess = receipt.status === 'completed'
  const isFailed = receipt.status === 'failed'
  const isProcessing = !isFinal

  return (
    <div className="container mx-auto px-4 py-6 max-w-md">
      {/* Main receipt card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden animate-fade-in">
        
        {/* Top colored bar */}
        <div className={`h-1.5 ${isSuccess ? 'bg-emerald-500' : isFailed ? 'bg-red-500' : 'bg-accent'}`}></div>

        {/* Status section */}
        <div className="px-6 pt-8 pb-6 text-center">
          {isSuccess && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500 flex items-center justify-center">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-text-primary mb-1">Payment Successful</h1>
              <p className="text-text-muted text-sm">Your redemption has been completed</p>
            </>
          )}

          {isFailed && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500 flex items-center justify-center">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-text-primary mb-1">Order Failed</h1>
              <p className="text-text-muted text-sm">Don't worry — your code is still valid and can be used again</p>
            </>
          )}

          {isProcessing && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 relative">
                <svg className="animate-spin" viewBox="0 0 50 50" style={{ width: 64, height: 64 }}>
                  <circle cx="25" cy="25" r="20" fill="none" stroke="#e5e7eb" strokeWidth="4"></circle>
                  <circle cx="25" cy="25" r="20" fill="none" stroke="#2563eb" strokeWidth="4" strokeDasharray="80" strokeDashoffset="60" strokeLinecap="round"></circle>
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-text-primary mb-1">Processing</h1>
              <p className="text-text-muted text-sm">Your order is being processed</p>
            </>
          )}
        </div>

        {/* Divider with cut-out circles */}
        <div className="relative">
          <div className="border-t border-dashed border-gray-200 mx-6"></div>
          <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-background rounded-full"></div>
          <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-background rounded-full"></div>
        </div>

        {/* Details section */}
        <div className="px-6 py-5">
          {/* Product & Value - prominent */}
          <div className="text-center mb-5">
            <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Product</p>
            <p className="text-lg font-bold text-text-primary">{receipt.product_name}</p>
            <p className="text-2xl font-extrabold text-accent mt-1">{receipt.denomination}</p>
          </div>

          {/* Detail rows */}
          <div className="space-y-3">
            {receipt.user_input && (
              <div className="flex justify-between">
                <span className="text-text-muted text-sm">Delivered to</span>
                <span className="text-text-primary text-sm font-semibold">{receipt.user_input}</span>
              </div>
            )}
            {receipt.code_masked && (
              <div className="flex justify-between">
                <span className="text-text-muted text-sm">Code</span>
                <span className="text-text-secondary text-sm font-mono">{receipt.code_masked}</span>
              </div>
            )}
            {receipt.redeemed_at && (
              <div className="flex justify-between">
                <span className="text-text-muted text-sm">Date</span>
                <span className="text-text-secondary text-sm">{formatDate(receipt.redeemed_at)}</span>
              </div>
            )}
            {receipt.completed_at && (
              <div className="flex justify-between">
                <span className="text-text-muted text-sm">Completed</span>
                <span className="text-text-secondary text-sm">{formatDate(receipt.completed_at)}</span>
              </div>
            )}
          </div>

          {isFailed && receipt.error_message && (
            <div className="mt-4 bg-red-50 rounded-lg p-3">
              <p className="text-red-600 text-sm">{receipt.error_message}</p>
            </div>
          )}

          {isProcessing && (
            <div className="mt-4 flex items-center justify-center space-x-2 text-accent text-xs">
              <div className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse"></div>
              <span>Updating automatically</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 space-y-2">
          {isFailed && (
            <button
              onClick={() => navigate(`/redeem/${receipt.category || 'telegram'}`)}
              className="w-full py-2.5 rounded-lg bg-accent text-white text-sm font-medium text-center hover:bg-accent-dark transition-colors flex items-center justify-center"
            >
              <RotateCcw size={15} className="mr-1.5" />
              Try Again — Code Still Valid
            </button>
          )}

          <button onClick={copyLink} className="w-full py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-text-secondary hover:bg-gray-50 transition-colors flex items-center justify-center">
            {copied ? <><Check size={15} className="mr-1.5 text-emerald-500" />Copied!</> : <><Copy size={15} className="mr-1.5" />Copy Receipt Link</>}
          </button>

          {isSuccess && (
            <Link to="/" className="block w-full py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-text-secondary text-center hover:bg-gray-50 transition-colors">
              Redeem Another Code
            </Link>
          )}
        </div>

        {/* Receipt ID footer */}
        <div className="bg-gray-50 px-6 py-3 text-center border-t border-gray-100">
          <p className="text-[10px] text-text-muted uppercase tracking-wider mb-0.5">Receipt ID</p>
          <p className="text-[11px] text-text-muted font-mono select-all">{token}</p>
        </div>
      </div>

      {/* Bottom note */}
      <p className="text-center text-text-muted text-xs mt-4">
        Keep this link as your proof of redemption
      </p>
    </div>
  )
}

export default Receipt
